import { injectable } from 'inversify';

import { ProfileAccessModel, Restriction } from '@vp/profile/core/model/ProfileAccessModel';
import { ProfileEventModel } from '@vp/profile/core/model/ProfileEventModel';
import { ProfileHonorRecordModel } from '@vp/profile/core/model/ProfileHonorRecordModel';
import { ProfileModel } from '@vp/profile/core/model/ProfileModel';
import { ProfilePhotoModel, ProfilePhotoType } from '@vp/profile/core/model/ProfilePhotoModel';
import { ProfileThemeMode, ProfileThemeModel } from '@vp/profile/core/model/ProfileThemeModel';
import { ProfileVideoModel } from '@vp/profile/core/model/ProfileVideoModel';
import { ImageResponseDto } from '@vp/profile/data/dto/ImageResponseDto';
import { ProfileResponseDto, RestrictedSection } from '@vp/profile/data/dto/ProfileResponseDto';
import { TributeResponseDto } from '@vp/profile/data/dto/TributeResponseDto';
import { VideoResponseDto } from '@vp/profile/data/dto/VideoResponseDto';

import { LiveMediaResponseDto } from './dto/LiveMediaResponseDto';
import { ProfileLiveMediaModel } from '../core/model/ProfileLiveMediaModel';

@injectable()
export class ProfileDataMapper {
  private readonly restrictionMap: Record<RestrictedSection, Restriction> = {
    [RestrictedSection.Bio]: Restriction.Bio,
    [RestrictedSection.Timeline]: Restriction.Events,
    [RestrictedSection.Tributes]: Restriction.Honor,
    [RestrictedSection.Gallery]: Restriction.Gallery,
  };

  private themeModes: Set<ProfileThemeMode> = new Set(['dark', 'light']);

  toProfile(dto: ProfileResponseDto): ProfileModel {
    return {
      id: dto.id,
      name: dto.name,
      bio: dto.bio ?? '',
      birthDate: dto.date_of_birth ?? '',
      deathDate: dto.date_of_death ?? '',
      image: this.toImage(dto),
      photos: this.toPhotos(dto),
      videos: this.toVideos(dto),
      events: this.toEvents(dto),
      honorRecords: this.toHonorRecords(dto),
      theme: this.toTheme(dto),
      slideshowAudio: dto.settings?.slideshow_melody ?? '',
      active: dto.active,
      premium: dto.premium ?? false,
      access: this.toAccess(dto, 'viewer'),
      liveMedia: this.toLiveMedia(dto.live_media),
    };
  }

  toOwnerProfile(dto: ProfileResponseDto): ProfileModel {
    const model = this.toProfile(dto);
    return { ...model, access: this.toAccess(dto, 'owner') };
  }

  toHonorRecord(tribute: TributeResponseDto): ProfileHonorRecordModel {
    return {
      id: tribute.id.toString(),
      author: tribute.author,
      text: tribute.text,
      date: tribute.created_at,
      approved: tribute.approved,
    };
  }

  toPhoto(image: ImageResponseDto): ProfilePhotoModel {
    return {
      id: image.id.toString(),
      hash: image.blurhash,
      url: image.url,
      type: image.image_type ?? ProfilePhotoType.Static,
      liveStatus: image.status,
      staticUrl: image.static_url,
    };
  }

  toVideo(video: VideoResponseDto): ProfileVideoModel {
    return {
      id: video.id.toString(),
      url: video.url,
      thumbnail: video.thumbnail,
      thumbnailHash: video.blurhash,
    };
  }

  toLiveMedia(liveMedia?: LiveMediaResponseDto): ProfileLiveMediaModel {
    return new ProfileLiveMediaModel({
      profileImageUsageCounter: liveMedia?.avatar_usage_counter ?? 0,
      maxProfileImageCount: liveMedia?.max_avatar_count ?? 0,
      photosUsageCounter: liveMedia?.images_usage_counter ?? 0,
      maxPhotosCount: liveMedia?.max_images_count ?? 0,
    } as ProfileLiveMediaModel);
  }

  private toPhotos({ images = [] }: ProfileResponseDto): ProfilePhotoModel[] {
    return images.map(image => this.toPhoto(image));
  }

  private toEvents({ timelines = [] }: ProfileResponseDto): ProfileEventModel[] {
    return timelines.map(timeline => {
      const id = timeline.id.toString();
      const image = timeline.image?.url ?? '';
      const hash = timeline.image?.blurhash ?? '';

      return { id, date: timeline.date, description: timeline.description, hash, image };
    });
  }

  private toVideos({ videos = [] }: ProfileResponseDto): ProfileVideoModel[] {
    return videos.map(video => this.toVideo(video));
  }

  private toHonorRecords({ tributes = [] }: ProfileResponseDto): ProfileHonorRecordModel[] {
    return tributes.filter(({ approved }) => approved).map(tribute => this.toHonorRecord(tribute));
  }

  private toImage(dto: ProfileResponseDto): ProfilePhotoModel {
    return {
      id: dto.avatar?.id?.toString() ?? '',
      hash: dto.avatar?.blurhash ?? '',
      url: dto.avatar?.url ?? '',
      type: dto.avatar?.image_type ?? ProfilePhotoType.Static,
      liveStatus: dto.avatar?.status,
      staticUrl: dto.avatar?.static_url,
    };
  }

  private toTheme(dto: ProfileResponseDto): ProfileThemeModel {
    const isValid = this.themeModes.has(dto.settings?.background_mode as ProfileThemeMode);
    const mode = isValid ? (dto.settings?.background_mode as ProfileThemeMode) : 'dark';
    return { color: null, mode };
  }

  private toAccess(dto: ProfileResponseDto, mode: 'owner' | 'viewer'): ProfileAccessModel {
    let set = new Set<Restriction>();
    const sections = mode === 'viewer' ? dto.restricted_sections : dto.settings?.restricted_sections;
    const isFullyRestricted = mode === 'viewer' ? dto.fully_restricted : dto.settings?.fully_restricted;

    if (!isFullyRestricted) {
      for (const section of sections ?? []) {
        const restriction = this.restrictionMap[section];
        restriction != null && set.add(restriction);
      }
    } else {
      set = new Set(ProfileAccessModel.allRestrictions);
    }

    const code = dto.settings?.restricted_access_pin ?? '';
    return new ProfileAccessModel(set, code);
  }
}
